import { useEffect, useState } from "react";
import { CloudArrowUp, HardDrives, FloppyDisk } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function SettingsPanel() {
  const [cfg, setCfg] = useState(null);
  const [provider, setProvider] = useState("objstore");
  const [folderId, setFolderId] = useState("");
  const [saJson, setSaJson] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/storage-config").then((r) => {
      setCfg(r.data);
      setProvider(r.data.provider);
      setFolderId(r.data.drive_folder_id || "");
    }).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/storage-config", {
        provider,
        drive_folder_id: folderId,
        ...(saJson.trim() ? { service_account_json: saJson.trim() } : {}),
      });
      toast.success("Storage configuration saved");
      setSaJson("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!cfg) return null;

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-panel">
      <div className="rounded-sm border border-border bg-card p-6 sm:p-8">
        <h3 className="font-display text-xl font-bold tracking-tight">Storage Configuration</h3>
        <div className="mt-1 text-sm text-muted-foreground">
          All uploads (vehicle photos, NRIC, accident scenes, licenses, invoices, payslips) go to the active destination.
          Current: <Badge variant="outline" className="ml-1 rounded-sm font-mono font-bold uppercase text-primary" data-testid="storage-current-provider">{cfg.provider === "drive" ? "Google Drive" : "Object Storage"}</Badge>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button type="button" data-testid="provider-objstore" onClick={() => setProvider("objstore")}
            className={`rounded-sm border p-5 text-left transition-colors ${provider === "objstore" ? "border-primary bg-primary/5" : "border-input bg-background hover:border-primary/40"}`}>
            <HardDrives size={26} className="text-primary" />
            <p className="mt-3 font-display text-sm font-bold">Emergent Object Storage</p>
            <p className="mt-1 text-xs text-muted-foreground">Built-in managed storage. No configuration needed.</p>
          </button>
          <button type="button" data-testid="provider-drive" onClick={() => setProvider("drive")}
            className={`rounded-sm border p-5 text-left transition-colors ${provider === "drive" ? "border-primary bg-primary/5" : "border-input bg-background hover:border-primary/40"}`}>
            <CloudArrowUp size={26} className="text-primary" />
            <p className="mt-3 font-display text-sm font-bold">Google Drive</p>
            <p className="mt-1 text-xs text-muted-foreground">Upload everything to your own Google Drive via a Service Account.</p>
          </button>
        </div>

        {provider === "drive" && (
          <div className="mt-6 space-y-5 rounded-sm border border-primary/30 bg-primary/5 p-5" data-testid="drive-config-section">
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Service Account JSON Key</Label>
              <Textarea data-testid="sa-json-input" value={saJson} onChange={(e) => setSaJson(e.target.value)}
                placeholder={cfg.has_credentials ? `Configured (${cfg.service_account_email}) — paste new JSON to replace` : 'Paste the full service account JSON key here'}
                rows={6} className="rounded-sm border-input bg-background font-mono text-xs focus-visible:ring-primary" />
              {cfg.has_credentials && (
                <p className="text-xs text-emerald-400" data-testid="sa-configured-note">
                  Credentials saved for: <span className="font-mono">{cfg.service_account_email}</span>
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Drive Folder ID (optional)</Label>
              <Input data-testid="drive-folder-input" value={folderId} onChange={(e) => setFolderId(e.target.value)}
                placeholder="e.g. 1AbCDefGhIJKlmnOpQRstuVWxyz"
                className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
            </div>
            <div className="rounded-sm border border-input bg-background p-4 text-xs leading-relaxed text-muted-foreground">
              <p className="font-bold uppercase tracking-widest text-foreground">Setup steps</p>
              <ol className="mt-2 list-decimal space-y-1 pl-4">
                <li>Google Cloud Console → create a project → enable the <b>Google Drive API</b>.</li>
                <li>Create a <b>Service Account</b> and download its JSON key.</li>
                <li>In Google Drive (psstowing.cs@gmail.com), share the target folder with the service account email as <b>Editor</b>.</li>
                <li>Paste the JSON above and optionally the folder ID (from the folder URL).</li>
              </ol>
            </div>
          </div>
        )}

        <Button data-testid="storage-save-button" onClick={save} disabled={busy}
          className="mt-6 rounded-sm bg-primary px-8 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
          <FloppyDisk size={16} className="mr-2" /> {busy ? "Saving..." : "Save Configuration"}
        </Button>
      </div>
    </div>
  );
}
